import { formatDateTime } from '~/lib/local-datetime';
import type { GitLocalVersion, GitNeedsAction } from './publish-stream';

/**
 * FRONT-3411: the one wording of a reconcile review, shared by the terminal
 * screen and the Save App dialog so the two can never disagree. A review is a
 * checkout review (resume the local work or start from the app) when the server
 * sent `localVersion` (src/pro-builder/git-lifecycle.ts); which side is older is
 * judged from content only, never from file or commit times.
 */
export type CheckoutSide = 'local' | 'current';

export interface ReconcileOption {
  choice: CheckoutSide;
  label: string;
  /** What pressing it does, in a sentence; null for a review without its own wording. */
  detail: string | null;
  /** Filled and focused: only when the content proves this is the side to take, or it is the only way on. */
  primary: boolean;
}

export interface ReconcileView {
  options: ReconcileOption[];
}

/** A checkout review's own words (FRONT-3477), shown on the terminal screen and in Save App alike. */
export interface CheckoutReviewCopy {
  title: string;
  /** The sentence; `time`, when there is one, sits between `before` and `after` and is shown emphasised. */
  sentence: { before: string; time: string | null; after: string };
  /** In the order shown; the first is the primary one. */
  options: ReconcileOption[];
  /** The line under the buttons; `lead` is shown emphasised before `text`. */
  note: { lead: string | null; text: string };
  filesLabel: string;
  /** Per file, whether it is new work or an older copy; null when the review does not mark files. */
  fileLabels: Record<string, string> | null;
}

const PURGED_LABEL = 'Open the current saved version';
const PURGED_DETAIL = 'Starts from the current saved version. Your local copy predates a change to the app’s history, so it is kept as a local recovery copy for reference and is not saved back.';

const FILE_LABELS = { new: 'New work', older: 'Older copy' } as const;

/**
 * Which side leads follows `localVersion.kind` alone, judged file by file by content on the server: every file an
 * older copy leads with the app, any new work leads with the terminal, and an unchecked history leads with neither.
 * `displayChangedAt` only fills the sentence: it is never compared with `savedAt` or used to pick a side.
 */
export function checkoutReviewCopy(action: GitNeedsAction, format: (iso: string) => string = (iso) => formatDateTime(iso)): CheckoutReviewCopy | null {
  const v = action.localVersion;
  if (!v || action.code === 'purged' || !action.actions?.includes('reconcile')) return null;
  const filesLabel = `See what changed · ${action.paths.length === 1 ? '1 file' : `${action.paths.length} files`}`;
  if (v.kind === 'older-saved') return {
    title: 'Your local copy is older than the app',
    sentence: { before: 'Every file in this terminal matches an earlier saved version of the app, the latest from ', time: format(v.savedAt), after: '. The app has changed since.' },
    options: [
      { choice: 'current', label: 'Start from the current version', detail: null, primary: true },
      { choice: 'local', label: 'Resume the older copy', detail: null, primary: false },
    ],
    note: { lead: null, text: 'Nothing is deleted either way.' },
    filesLabel,
    fileLabels: null,
  };
  const time = action.displayChangedAt ? format(action.displayChangedAt) : null;
  const checked = v.kind === 'new-work';
  let sentence: CheckoutReviewCopy['sentence'];
  if (checked) {
    sentence = time
      ? { before: 'You have work in this terminal from ', time, after: " that isn't saved to the app yet." }
      : { before: "You have work in this terminal that isn't saved to the app yet.", time: null, after: '' };
  } else {
    sentence = time
      ? { before: 'Your terminal has work from ', time, after: " that's different from the app. EDDY couldn't check whether it's newer." }
      : { before: "Your terminal has work that's different from the app. EDDY couldn't check whether it's newer.", time: null, after: '' };
  }
  return {
    title: 'Pick up where you left off?',
    sentence,
    options: [
      { choice: 'local', label: 'Resume my work', detail: null, primary: checked },
      { choice: 'current', label: 'Start fresh', detail: null, primary: false },
    ],
    note: { lead: 'Start fresh', text: checked ? " loads the app as it was last saved. Your unsaved work isn't deleted." : " loads the app as it was last saved. Your work isn't deleted." },
    filesLabel,
    fileLabels: checked ? Object.fromEntries(Object.entries(v.files).map(([path, verdict]) => [path, FILE_LABELS[verdict]])) : null,
  };
}

/** The choice buttons a reconcile review offers, in order; empty when it offers none. */
export function reconcileView(action: GitNeedsAction): ReconcileView {
  if (!action.actions?.includes('reconcile')) return { options: [] };
  if (action.code === 'purged') return { options: [{ choice: 'current', label: PURGED_LABEL, detail: PURGED_DETAIL, primary: true }] };
  const checkout = checkoutReviewCopy(action);
  if (checkout) return { options: checkout.options };
  return { options: [
    { choice: 'local', label: 'Reconcile using my local versions', detail: null, primary: false },
    { choice: 'current', label: 'Keep current app versions', detail: null, primary: false },
  ] };
}

/** The terminal screen's heading, lead sentence and file-list label for a review that is not a checkout review. */
export function terminalReviewCopy(action: GitNeedsAction): { title: string; lead: string; filesLabel: string } {
  if (action.code === 'purged') return { title: 'This app’s history changed', lead: action.message, filesLabel: 'Files removed from the app’s history' };
  return { title: 'This app needs a decision before its terminal starts', lead: action.message, filesLabel: 'Files involved' };
}

/** The review a refused terminal open carries (`git` on its 409 body), or null for any other failure. */
export function terminalCheckoutReview(body: unknown): ({ folder: string } & GitNeedsAction) | null {
  if (!body || typeof body !== 'object') return null;
  const git = (body as { git?: unknown }).git;
  if (!git || typeof git !== 'object') return null;
  const g = git as Record<string, unknown>;
  if (typeof g.folder !== 'string' || typeof g.message !== 'string' || typeof g.code !== 'string' || !Array.isArray(g.paths)) return null;
  return git as { folder: string } & GitNeedsAction;
}
